import type { Editor } from '@tiptap/core'
import { findManualInstructionMatches } from './manual-instruction'
import { findInsufficientInformationMatches } from './insufficient-information'
import { findPlaceholderMatches } from './not-specified'

/**
 * After setContent(markdown), applies TipTap marks for:
 * - `[NOT_FOUND]` and legacy `[NOT_SPECIFIED]` / `[NOT_FOUND_IN_DOCUMENTS]` (notSpecified)
 * - `[MANUAL_INSTRUCTION: …]` (manualInstruction)
 * - `[INSUFFICIENT_INFORMATION]` (insufficientInformation)
 *
 * Single document traversal and one dispatch.
 */
export function applyCompliancePlaceholderMarks(editor: Editor | null): void {
  if (!editor) {
    return
  }

  const { state } = editor
  const { doc, tr } = state
  const notSpecifiedType = state.schema.marks.notSpecified
  const manualInstructionType = state.schema.marks.manualInstruction
  const insufficientInformationType = state.schema.marks.insufficientInformation
  if (
    !notSpecifiedType &&
    !manualInstructionType &&
    !insufficientInformationType
  ) {
    return
  }

  let modified = false

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return
    }

    const text = node.text

    if (notSpecifiedType) {
      for (const pm of findPlaceholderMatches(text)) {
        const from = pos + pm.index
        const to = from + pm.length
        const mark = notSpecifiedType.create({
          placeholder: pm.placeholder,
          fieldLabel: pm.fieldLabel,
          guidance: pm.guidance,
        })
        tr.addMark(from, to, mark)
        modified = true
      }
    }

    if (manualInstructionType) {
      for (const mm of findManualInstructionMatches(text)) {
        const from = pos + mm.index
        const to = from + mm.length
        const mark = manualInstructionType.create({
          instruction: mm.instruction,
        })

        tr.addMark(from, to, mark)
        modified = true
      }
    }

    if (insufficientInformationType) {
      for (const im of findInsufficientInformationMatches(text)) {
        const from = pos + im.index
        const to = from + im.length
        const mark = insufficientInformationType.create()

        tr.addMark(from, to, mark)
        modified = true
      }
    }
  })

  if (modified) {
    editor.view.dispatch(tr)
  }
}
